/**
 * Geo-Location Service
 * Handles automatic location detection, store assignment, and content visibility
 * 
 * Usage in sections:
 * Add data-geo-selected attribute to the first tag of each section:
 * 
 * - data-geo-selected="pickup" - Section only visible when user is within radius of a store
 * - data-geo-selected="any" - Section visible regardless of location
 * 
 * Optional attributes:
 * - data-geo-store-id="123" - Specific store location ID (uses assigned store if not set)
 * - data-geo-radius="50" - Custom radius in miles (uses store default if not set)
 * 
 * Example:
 * <div id="Section-{{ section.id }}" data-geo-selected="pickup" data-geo-radius="25">
 */

class GeoLocationService {
  constructor() {
    this.storageKeys = {
      userLocation: 'geo_user_location',
      assignedStore: 'geo_assigned_store',
      locationOverride: 'geo_location_override',
      lastUpdate: 'geo_last_update'
    };
    
    this.updateInterval = 24 * 60 * 60 * 1000; // 24 hours
    this.stores = [];
    this.currentLocation = null;
    this.assignedStore = null;
    this.isManualOverride = false;
    
    this.init();
  }

  /**
   * Initialize the service
   */
  async init() {
    // Load store data from Shopify
    await this.loadStoreData();
    
    // Check for manual override first
    const override = this.getStoredLocationOverride();
    if (override) {
      this.isManualOverride = true;
      this.currentLocation = override;
      this.assignedStore = this.findNearestStoreWithinRadius(override.lat, override.lng);
      this.updateContentVisibility();
      return;
    }
    
    // Check for cached location
    const cachedLocation = this.getStoredLocation();
    const cachedStore = this.getStoredStore();
    const lastUpdate = this.getLastUpdate();
    const hasCachedStore = localStorage.getItem(this.storageKeys.assignedStore) !== null;
    
    if (cachedLocation && hasCachedStore && lastUpdate) {
      const timeSinceUpdate = Date.now() - lastUpdate;
      if (timeSinceUpdate < this.updateInterval) {
        this.currentLocation = cachedLocation;
        this.assignedStore = cachedStore || null;
        this.updateContentVisibility();
        return;
      }
    }
    
    // Fetch new location
    await this.detectLocation();
  }

  /**
   * Load store data from geo-location-selector section
   * Store coordinates and radius are configured in the Geo Location Selector section settings
   * Data is injected via window.geoStores from geo-location-selector.liquid
   */
  async loadStoreData() {
    try {
      // Store data is injected via Liquid template from geo-location-selector section
      // No need to fetch from API - it's already available in window.geoStores
      if (window.geoStores && Array.isArray(window.geoStores)) {
        this.stores = window.geoStores.map(store => {
          // Validate store data
          if (!store.latitude || !store.longitude) {
            console.warn('GeoLocation: Store missing coordinates', store);
            return null;
          }
          
          return {
            id: store.id,
            name: store.name || 'Unknown Store',
            lat: parseFloat(store.latitude),
            lng: parseFloat(store.longitude),
            radius: store.radius || 50, // Default 50 miles
            locationId: store.location_id || store.id
          };
        }).filter(store => store !== null); // Remove invalid stores
      } else {
        console.warn('GeoLocation: No store data available. Make sure geo-location-selector section is configured with latitude and longitude.');
      }
    } catch (error) {
      // Silently handle errors - don't show popups
      console.warn('GeoLocation: Could not load store data', error);
    }
  }

  /**
   * Detect user location via IP
   */
  async detectLocation() {
    try {
      const response = await fetch('https://ipapi.co/json/?key=A92NZS11jjV8kFUlcQZRc2s7QdHDc3EmdGngOVzbzRH1dVhmOF', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check for API errors
      if (data.error) {
        throw new Error(data.reason || 'API error');
      }
      
      if (data.latitude && data.longitude) {
        this.currentLocation = {
          lat: parseFloat(data.latitude),
          lng: parseFloat(data.longitude),
          city: data.city || '',
          region: data.region || '',
          country: data.country_name || ''
        };
        
        // Find nearest store
        this.assignedStore = this.findNearestStoreWithinRadius(this.currentLocation.lat, this.currentLocation.lng);
        
        // Store in localStorage
        this.storeLocation(this.currentLocation);
        this.storeStore(this.assignedStore);
        this.setLastUpdate(Date.now());
        
        // Update content visibility
        this.updateContentVisibility();
        
        // Dispatch event
        this.dispatchLocationUpdate();
      } else {
        throw new Error('No location data in response');
      }
    } catch (error) {
      // Silently handle errors - don't show popups or break the page
      console.warn('GeoLocation: Could not detect location', error);
      // Fallback: apply default geo rules (shop/ecom) instead of showing every geo variant.
      this.updateContentVisibility();
    }
  }

  /**
   * Find nearest store to given coordinates
   */
  findNearestStore(lat, lng) {
    if (!this.stores || this.stores.length === 0) {
      return null;
    }
    
    let nearestStore = null;
    let minDistance = Infinity;
    
    this.stores.forEach(store => {
      const distance = this.calculateDistance(lat, lng, store.lat, store.lng);
      if (distance < minDistance) {
        minDistance = distance;
        nearestStore = { ...store, distance };
      }
    });
    
    return nearestStore;
  }

  findNearestStoreWithinRadius(lat, lng) {
    const nearestStore = this.findNearestStore(lat, lng);
    if (!nearestStore) {
      return null;
    }

    const storeRadius = nearestStore.radius || 50;
    if (nearestStore.distance > storeRadius) {
      return null;
    }

    return nearestStore;
  }

  /**
   * Calculate distance between two coordinates (Haversine formula)
   * Returns distance in miles
   */
  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Check if user is within radius of a store
   */
  isWithinRadius(storeId, radius = null) {
    if (!this.currentLocation || !this.assignedStore) {
      return false;
    }
    
    if (this.assignedStore.id === storeId || this.assignedStore.locationId === storeId) {
      const storeRadius = radius || this.assignedStore.radius || 50;
      return this.assignedStore.distance <= storeRadius;
    }
    
    return false;
  }

  /**
   * Update content visibility based on location
   */
  updateContentVisibility(showAll = false) {
    if (showAll) {
      // Show all geo-aware content if location detection failed
      document.querySelectorAll('[data-geo-aware]').forEach(el => {
        el.classList.remove('geo-hidden');
      });
      // Show all sections with data-geo-selected
      document.querySelectorAll('[data-geo-selected]').forEach(el => {
        el.classList.remove('geo-hidden');
        el.classList.remove('geo-pickup-pending');
      });
      return;
    }
    
    // Process all geo-aware elements (legacy support)
    document.querySelectorAll('[data-geo-aware]').forEach(el => {
      const storeId = el.getAttribute('data-geo-store-id');
      const radius = el.getAttribute('data-geo-radius') ? parseFloat(el.getAttribute('data-geo-radius')) : null;
      const hideOutside = el.getAttribute('data-geo-hide-outside') === 'true';
      
      if (storeId) {
        const isWithin = this.isWithinRadius(storeId, radius);
        
        if (hideOutside) {
          // Hide if outside radius
          if (isWithin) {
            el.classList.remove('geo-hidden');
          } else {
            el.classList.add('geo-hidden');
          }
        } else {
          // Show if within radius
          if (isWithin) {
            el.classList.remove('geo-hidden');
          } else {
            el.classList.add('geo-hidden');
          }
        }
      } else {
        // No store ID specified, check if user has assigned store
        if (this.assignedStore) {
          el.classList.remove('geo-hidden');
        } else {
          el.classList.add('geo-hidden');
        }
      }
    });
    
    // Process sections with data-geo-selected attribute
    document.querySelectorAll('[data-geo-selected]').forEach(section => {
      const selected = section.getAttribute('data-geo-selected');
      const storeId = section.getAttribute('data-geo-store-id');
      const radius = section.getAttribute('data-geo-radius') ? parseFloat(section.getAttribute('data-geo-radius')) : null;
      
      // Remove pending class - location calculation is done
      section.classList.remove('geo-pickup-pending');
      
      // If selected is "any", check for exclusion rules
      if (selected === 'any') {
        const exclude = section.getAttribute('data-geo-exclude');
        
        // If no exclusion is set or it's "dont-hide", show section for everyone
        if (!exclude || exclude === 'dont-hide') {
          section.classList.remove('geo-hidden');
          return;
        }
        
        const audienceStoreKey = this.getAudienceStoreKey();

        // No location detected and no explicit assignment: fall back to shop/ecom default.
        if (!audienceStoreKey) {
          section.classList.toggle('geo-hidden', exclude !== 'hide-both');
          return;
        }

        // Exclusion values are treated as mutually exclusive audience switches:
        // - hide-hartville  => middlefield audience only
        // - hide-middlefield => hartville audience only
        // - hide-both => shop/ecom audience only
        if (exclude === 'hide-hartville') {
          section.classList.toggle('geo-hidden', audienceStoreKey !== 'middlefield');
        } else if (exclude === 'hide-middlefield') {
          section.classList.toggle('geo-hidden', audienceStoreKey !== 'hartville');
        } else if (exclude === 'hide-both') {
          section.classList.toggle('geo-hidden', audienceStoreKey !== 'shop');
        } else {
          // Unknown exclude value - fail safe to hidden
          section.classList.add('geo-hidden');
        }
        return;
      }
      
      // If selected is a specific store (e.g., "only-pickup-hartville", "only-pickup-middlefield")
      if (selected && selected.startsWith('only-pickup-')) {
        if (!this.currentLocation) {
          // No location detected, hide section
          section.classList.add('geo-hidden');
          return;
        }
        
        // Extract store name from the selected value
        const storeName = selected.replace('only-pickup-', '');
        const isWithinStoreRadius = this.isWithinStoreRadius(storeName);
        
        if (isWithinStoreRadius) {
          section.classList.remove('geo-hidden');
        } else {
          section.classList.add('geo-hidden');
        }
        return;
      }
      
      // Legacy support: If selected is "pickup", check if user is within radius
      if (selected === 'pickup') {
        const explicitAssignedStoreKey = this.getExplicitAssignedStoreKey();

        // Explicitly choosing ecom/main/shop must hide pickup sections.
        if (explicitAssignedStoreKey === 'shop') {
          section.classList.add('geo-hidden');
          return;
        }

        if (!this.currentLocation || !this.assignedStore) {
          // No location detected, hide section
          section.classList.add('geo-hidden');
          return;
        }

        const nearestPickupStoreKey = this.getNearestPickupStoreWithinRadius();

        // Check if within radius of specified store or assigned store
        if (storeId) {
          const isWithin = this.isWithinRadius(storeId, radius);
          if (isWithin) {
            section.classList.remove('geo-hidden');
          } else {
            section.classList.add('geo-hidden');
          }
        } else {
          // If explicit pickup store chosen, show pickup sections.
          if (explicitAssignedStoreKey === 'hartville' || explicitAssignedStoreKey === 'middlefield') {
            section.classList.remove('geo-hidden');
            return;
          }

          if (nearestPickupStoreKey) {
            section.classList.remove('geo-hidden');
          } else {
            section.classList.add('geo-hidden');
          }
        }
      } else {
        // Unknown value, hide by default
        section.classList.add('geo-hidden');
      }
    });
    
    // Process products with only-pickup tag
    this.updateProductVisibility();
    
    // Dispatch event for other scripts
    document.dispatchEvent(new CustomEvent('geo:visibility-updated', {
      detail: {
        location: this.currentLocation,
        store: this.assignedStore
      }
    }));
  }

  normalizeStoreKey(value) {
    if (!value) return '';
    const normalized = String(value).toLowerCase().trim();
    if (normalized.includes('hartville')) return 'hartville';
    if (normalized.includes('middlefield')) return 'middlefield';
    if (
      normalized.includes('shop') ||
      normalized.includes('online') ||
      normalized.includes('warehouse') ||
      normalized.includes('shipping') ||
      normalized.includes('ecom') ||
      normalized.includes('e-commerce') ||
      normalized.includes('main store') ||
      normalized.includes('main')
    ) return 'shop';
    return normalized;
  }

  getExplicitAssignedStoreKey() {
    try {
      const stored = localStorage.getItem(this.storageKeys.assignedStore);
      if (!stored) return '';
      const parsed = JSON.parse(stored);
      const key = this.normalizeStoreKey(parsed?.key);
      if (key === 'hartville' || key === 'middlefield' || key === 'shop') return key;
    } catch (e) {
      // ignore
    }
    return '';
  }

  isWithinStoreRadius(storeName) {
    const explicitAssignedStoreKey = this.getExplicitAssignedStoreKey();
    const requestedStoreKey = this.normalizeStoreKey(storeName);

    // Explicit store choice takes precedence over radius checks.
    if (explicitAssignedStoreKey) {
      if (explicitAssignedStoreKey === 'shop') {
        return false;
      }
      if (explicitAssignedStoreKey === 'hartville' || explicitAssignedStoreKey === 'middlefield') {
        return explicitAssignedStoreKey === requestedStoreKey;
      }
    }

    if (!this.currentLocation) {
      return false;
    }

    const nearestPickupStoreKey = this.getNearestPickupStoreWithinRadius();
    if (nearestPickupStoreKey) {
      return nearestPickupStoreKey === requestedStoreKey;
    }

    const pickupStores = this.stores.filter((store) => {
      const key = this.normalizeStoreKey(store?.name);
      return key === requestedStoreKey;
    });

    return pickupStores.some((store) => {
      const distance = this.calculateDistance(this.currentLocation.lat, this.currentLocation.lng, store.lat, store.lng);
      const storeRadius = store.radius || 50;
      return distance <= storeRadius;
    });
  }

  getNearestPickupStoreWithinRadius() {
    if (!this.currentLocation || !this.stores || this.stores.length === 0) {
      return null;
    }

    const candidates = this.stores
      .map((store) => {
        const key = this.normalizeStoreKey(store?.name);
        if (key !== 'hartville' && key !== 'middlefield') return null;

        const distance = this.calculateDistance(
          this.currentLocation.lat,
          this.currentLocation.lng,
          store.lat,
          store.lng
        );

        const radius = store.radius || 50;
        if (distance > radius) return null;

        return { key, distance };
      })
      .filter(Boolean)
      .sort((a, b) => a.distance - b.distance);

    const hartvillePreferenceMiles = this.getHartvillePreferenceMiles();

    if (candidates.length > 1) {
      const best = candidates[0];
      const second = candidates[1];
      if (second && Math.abs(second.distance - best.distance) <= hartvillePreferenceMiles) {
        const hartvilleCandidate = candidates.find((candidate) => candidate.key === 'hartville');
        if (hartvilleCandidate) {
          return hartvilleCandidate.key;
        }
      }
    }

    return candidates.length ? candidates[0].key : null;
  }

  getHartvillePreferenceMiles() {
    const configured = parseFloat(window?.geoLocationConfig?.hartvillePreferenceMiles);
    if (Number.isFinite(configured) && configured >= 0) {
      return configured;
    }
    return 0.25;
  }

  getAudienceStoreKey() {
    const explicitAssignedStoreKey = this.getExplicitAssignedStoreKey();
    if (explicitAssignedStoreKey === 'hartville' || explicitAssignedStoreKey === 'middlefield' || explicitAssignedStoreKey === 'shop') {
      return explicitAssignedStoreKey;
    }

    if (!this.currentLocation) {
      return '';
    }

    const nearestPickupStoreKey = this.getNearestPickupStoreWithinRadius();
    if (nearestPickupStoreKey === 'hartville' || nearestPickupStoreKey === 'middlefield') {
      return nearestPickupStoreKey;
    }

    // Location known, but not inside pickup radii => shop/ecom audience.
    return 'shop';
  }

  updateProductVisibility() {
    const productsWithPickupOnly = document.querySelectorAll('[data-geo-pickup-only="true"]');

    if (productsWithPickupOnly.length === 0) {
      return;
    }

    productsWithPickupOnly.forEach((productElement) => {
      const isMainProductElement =
        (productElement.id && productElement.id.includes('MainProduct')) ||
        productElement.tagName === 'PRODUCT-INFO' ||
        productElement.closest('product-info');

      // Do not hide PDP main product content; geo behavior should not control PDP routing/visibility.
      if (isMainProductElement) {
        productElement.classList.remove('geo-product-hidden');
        return;
      }

      const pickupStoreName = productElement.getAttribute('data-geo-pickup-store');

      if (!pickupStoreName) {
        productElement.classList.add('geo-product-hidden');
        return;
      }

      if (!this.currentLocation) {
        productElement.classList.add('geo-product-hidden');
        return;
      }

      const isWithinStoreRadius = this.isWithinStoreRadius(pickupStoreName);

      if (isWithinStoreRadius) {
        productElement.classList.remove('geo-product-hidden');
      } else {
        productElement.classList.add('geo-product-hidden');
      }
    });
  }

  /**
   * Set manual location override
   */
  setManualLocation(lat, lng, address = '') {
    this.isManualOverride = true;
    this.currentLocation = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
      address: address || ''
    };
    
    // If no address provided, try to get it from reverse geocoding
    if (!address && window.google && window.google.maps) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat: parseFloat(lat), lng: parseFloat(lng) } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.currentLocation.address = results[0].formatted_address;
          // Update stored location with address
          localStorage.setItem(this.storageKeys.locationOverride, JSON.stringify(this.currentLocation));
          this.storeLocation(this.currentLocation);
          this.dispatchLocationUpdate();
        }
      });
    }
    
    this.assignedStore = this.findNearestStoreWithinRadius(lat, lng);
    
    // Store override
    localStorage.setItem(this.storageKeys.locationOverride, JSON.stringify(this.currentLocation));
    this.storeLocation(this.currentLocation);
    this.storeStore(this.assignedStore);
    this.setLastUpdate(Date.now());
    
    this.updateContentVisibility();
    this.dispatchLocationUpdate();
  }

  /**
   * Clear manual location override
   */
  clearManualLocation() {
    this.isManualOverride = false;
    localStorage.removeItem(this.storageKeys.locationOverride);
    localStorage.removeItem(this.storageKeys.userLocation);
    localStorage.removeItem(this.storageKeys.lastUpdate);
    // Reset to explicit shop/ecom default so "Clear Selection" is deterministic.
    localStorage.setItem(this.storageKeys.assignedStore, JSON.stringify({
      key: 'shop',
      name: 'Shop'
    }));
    this.currentLocation = null;
    this.assignedStore = null;
    this.updateContentVisibility();
    this.dispatchLocationUpdate();
    this.detectLocation();
  }

  /**
   * Get current location
   */
  getCurrentLocation() {
    return this.currentLocation;
  }

  /**
   * Get assigned store
   */
  getAssignedStore() {
    return this.assignedStore;
  }

  /**
   * Check if location is manually overridden
   */
  isLocationOverridden() {
    return this.isManualOverride;
  }

  // Storage methods
  storeLocation(location) {
    localStorage.setItem(this.storageKeys.userLocation, JSON.stringify(location));
  }

  getStoredLocation() {
    const stored = localStorage.getItem(this.storageKeys.userLocation);
    return stored ? JSON.parse(stored) : null;
  }

  storeStore(store) {
    let enrichedStore = store;

    try {
      const existing = JSON.parse(localStorage.getItem(this.storageKeys.assignedStore) || 'null');
      const existingKey = this.normalizeStoreKey(existing?.key);
      if (existingKey === 'hartville' || existingKey === 'middlefield' || existingKey === 'shop') {
        enrichedStore = { ...(store || {}), key: existingKey };
      }
    } catch (e) {
      // ignore invalid existing storage
    }

    localStorage.setItem(this.storageKeys.assignedStore, JSON.stringify(enrichedStore));
  }

  getStoredStore() {
    const stored = localStorage.getItem(this.storageKeys.assignedStore);
    return stored ? JSON.parse(stored) : null;
  }

  getStoredLocationOverride() {
    const stored = localStorage.getItem(this.storageKeys.locationOverride);
    return stored ? JSON.parse(stored) : null;
  }

  setLastUpdate(timestamp) {
    localStorage.setItem(this.storageKeys.lastUpdate, timestamp.toString());
  }

  getLastUpdate() {
    const stored = localStorage.getItem(this.storageKeys.lastUpdate);
    return stored ? parseInt(stored, 10) : null;
  }

  /**
   * Dispatch location update event
   */
  dispatchLocationUpdate() {
    document.dispatchEvent(new CustomEvent('geo:location-updated', {
      detail: {
        location: this.currentLocation,
        store: this.assignedStore,
        isManual: this.isManualOverride
      }
    }));
    this.updateProductVisibility();
  }
}

function isSearchOrCollectionPage() {
  const path = window.location.pathname || '';
  return path.includes('/search') || /^\/collections(\/|$)/.test(path);
}

function setupGeoDrivenSearchCollectionReload() {
  if (!isSearchOrCollectionPage()) return;
  if (window.__geoSearchCollectionReloadSetup) return;
  window.__geoSearchCollectionReloadSetup = true;

  const watchedKeys = new Set(['geo_assigned_store', 'geo_user_location', 'geo_location_override']);
  let reloadQueued = false;
  const lastValues = new Map();

  watchedKeys.forEach((key) => {
    try {
      lastValues.set(key, localStorage.getItem(key));
    } catch (e) {
      lastValues.set(key, null);
    }
  });

  const queueReload = () => {
    if (reloadQueued) return;
    reloadQueued = true;
    setTimeout(() => window.location.reload(), 0);
  };

  const evaluateChange = (key, nextValue) => {
    if (!watchedKeys.has(key)) return;
    const prevValue = lastValues.get(key);
    if (prevValue === nextValue) return;
    lastValues.set(key, nextValue);
    queueReload();
  };

  const originalSetItem = localStorage.setItem.bind(localStorage);
  localStorage.setItem = function(key, value) {
    originalSetItem(key, value);
    evaluateChange(key, String(value));
  };

  const originalRemoveItem = localStorage.removeItem.bind(localStorage);
  localStorage.removeItem = function(key) {
    originalRemoveItem(key);
    evaluateChange(key, null);
  };

  window.addEventListener('storage', (e) => {
    if (watchedKeys.has(e.key)) {
      evaluateChange(e.key, e.newValue);
    }
  });

  document.addEventListener('geo:location-updated', () => {
    watchedKeys.forEach((key) => {
      evaluateChange(key, localStorage.getItem(key));
    });
  }, true);
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.geoLocationService = new GeoLocationService();
      setupGeoDrivenSearchCollectionReload();
      
      document.addEventListener('geo:location-updated', () => {
        if (window.geoLocationService) {
          window.geoLocationService.updateProductVisibility();
        }
      });
      
      setTimeout(() => {
        if (window.geoLocationService) {
          window.geoLocationService.updateProductVisibility();
        }
      }, 1500);
    });
  } else {
    window.geoLocationService = new GeoLocationService();
    setupGeoDrivenSearchCollectionReload();
    
    document.addEventListener('geo:location-updated', () => {
      if (window.geoLocationService) {
        window.geoLocationService.updateProductVisibility();
      }
    });
    
    setTimeout(() => {
      if (window.geoLocationService) {
        window.geoLocationService.updateProductVisibility();
      }
    }, 1500);
  }
}
